import { parseQuery } from '@atlassiansox/code-search-query-parser';
import type { LexicalSearchFilter } from '@atlassiansox/code-search-query-parser';

import { captureExceptionWithTags } from 'src/utils/sentry';

import { FilterState } from '../components/filter-types';
import { FilterKey } from '../components/search-modifiers';

import { encodeQueryTerms } from './encode-query-terms';

/**
 * Maps FilterState keys to the attribute names used by the query parser.
 * Most keys match directly, but some differ (e.g. 'repo' → 'repository').
 */
const FILTER_KEY_TO_PARSER_ATTRIBUTE: Partial<
  Record<keyof FilterState, string>
> = {
  repo: 'repository',
};

function extractFilterValues(
  filter: LexicalSearchFilter | undefined,
  attribute: keyof FilterState
): string[] {
  if (!filter) return [];

  const parserAttribute =
    FILTER_KEY_TO_PARSER_ATTRIBUTE[attribute] ?? attribute;

  if ('eq' in filter) {
    return filter.attribute === parserAttribute ? [filter.eq] : [];
  }
  if ('and' in filter) {
    return filter.and.flatMap(f => extractFilterValues(f, attribute));
  }
  if ('or' in filter) {
    return filter.or.flatMap(f => extractFilterValues(f, attribute));
  }
  return [];
}

export function parseQParam(q: string): {
  searchTerm: string;
  filterState: FilterState;
} {
  const filterState: FilterState = {
    repo: [],
    path: [],
    extension: [],
    project: [],
    language: [],
  };

  if (!q.trim()) {
    return { searchTerm: '', filterState };
  }

  let parsed;
  try {
    parsed = parseQuery(q);
  } catch (e) {
    if (e instanceof Error) {
      captureExceptionWithTags(e);
    }
    return { searchTerm: q, filterState };
  }

  const searchTerm = encodeQueryTerms(parsed.terms);

  const filterKeys = Object.keys(filterState) as FilterKey[];
  for (const key of filterKeys) {
    filterState[key] = extractFilterValues(parsed.filter, key);
  }

  return { searchTerm, filterState };
}

export function buildQParam(
  searchTerm: string,
  filterState: FilterState
): string {
  const parts: string[] = [];

  if (searchTerm.trim()) {
    parts.push(searchTerm.trim());
  }

  if (filterState.repo.length > 0) {
    parts.push(...filterState.repo.map(r => `repo:${serializeValue(r)}`));
  }
  if (filterState.path.length > 0) {
    parts.push(...filterState.path.map(p => `path:${serializeValue(p)}`));
  }
  if (filterState.extension.length > 0) {
    parts.push(
      ...filterState.extension.map(e => `extension:${serializeValue(e)}`)
    );
  }
  if (filterState.project.length > 0) {
    parts.push(...filterState.project.map(p => `project:${serializeValue(p)}`));
  }
  if (filterState.language.length > 0) {
    parts.push(
      ...filterState.language.map(l => `language:${serializeValue(l)}`)
    );
  }

  return parts.join(' ');
}

/** Wraps a value in double-quotes if it contains spaces. */
function serializeValue(value: string): string {
  return value.includes(' ') ? `"${value}"` : value;
}
